import { articles, actualites, focus } from "./content";

/**
 * Source unique des métadonnées SEO.
 * Utilisée à deux endroits :
 *  - au build, par scripts/build-seo.mjs, pour prérendre un <head> par route ;
 *  - au runtime, par src/head.ts, pour que la navigation interne garde le head à jour.
 * Ne dépend d'aucune API navigateur : ce module doit rester importable dans Node.
 */

export const SITE_URL = "https://mavoixpourvillepin.fr";
export const SITE_NAME = "Ma voix pour de Villepin";
export const SITE_TAGLINE = "Initiative citoyenne indépendante";

export type HeadData = {
  path: string;
  title: string;
  description: string;
  canonical: string;
  type: "website" | "article";
  image: string;
  imageAlt: string;
  published?: string;
  modified?: string;
  noindex?: boolean;
  jsonLd: object[];
};

const BRAND_SUFFIX = ` | ${SITE_NAME}`;

/** Ajoute la marque au titre seulement si le résultat reste lisible dans un SERP. */
function withBrand(title: string, max = 68): string {
  return title.length + BRAND_SUFFIX.length <= max ? title + BRAND_SUFFIX : title;
}

const STATIC_PAGES: Record<string, { title: string; description: string }> = {
  "/": {
    title: `${SITE_NAME} — tribunes, actualités et capsules vidéo`,
    description:
      "Initiative citoyenne indépendante : tribunes, actualités sourcées et capsules vidéo pour une France libre, juste et indépendante.",
  },
  "/manifeste": {
    title: withBrand("Le manifeste"),
    description:
      "Le texte fondateur du collectif : le constat, la conviction, la méthode et l'indépendance.",
  },
  "/articles": {
    title: withBrand("Articles et réflexions"),
    description:
      "Tribunes et analyses de fond sur la diplomatie, la souveraineté, la justice sociale et le renouveau démocratique.",
  },
  "/actualites": {
    title: withBrand("Actualités"),
    description:
      "Le suivi daté et sourcé de la campagne présidentielle : déplacements, prises de parole et propositions de Dominique de Villepin.",
  },
  "/focus": {
    title: withBrand("Focus"),
    description:
      "Un dossier par idée : ce que Dominique de Villepin défend, confronté à ce qu'en disent la presse française et la presse étrangère.",
  },
  "/capsules": {
    title: withBrand("Capsules vidéo"),
    description: "Entretiens, capsules courtes et discours rassemblés pour nourrir le débat.",
  },
  "/qui-sommes-nous": {
    title: withBrand("Qui sommes-nous"),
    description:
      "Un collectif de citoyennes et citoyens réunis autour d'une exigence : que la voix de la France porte à nouveau.",
  },
  "/mentions-legales": {
    title: withBrand("Mentions légales"),
    description: "Informations éditoriales, indépendance et contact du site.",
  },
  "/confidentialite": {
    title: withBrand("Politique de confidentialité"),
    description: "Données personnelles, contact et mesure d'audience du site.",
  },
};

/* ───────── JSON-LD ───────── */

function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    email: "contact@mavoixpourvillepin.fr",
    foundingDate: "2026",
    description:
      "Initiative citoyenne indépendante, non affiliée à Dominique de Villepin ni à aucun parti politique.",
    disambiguatingDescription:
      "Collectif éditorial indépendant publiant tribunes, actualités et capsules vidéo. Non affilié à Dominique de Villepin ni à aucun parti.",
  };
}

function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description:
      "Initiative citoyenne indépendante : tribunes, actualités et capsules vidéo pour que la voix de la France porte à nouveau.",
    inLanguage: "fr-FR",
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

/** Dominique de Villepin est le sujet du site, jamais l'auteur ni l'éditeur. */
function subjectPersonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/#dominique-de-villepin`,
    name: "Dominique de Villepin",
    sameAs: ["https://fr.wikipedia.org/wiki/Dominique_de_Villepin"],
  };
}

function breadcrumbLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

function articleLd(opts: {
  headline: string;
  description: string;
  path: string;
  published: string;
  modified?: string;
  image?: string;
  keywords?: string[];
  section?: string;
  isNews?: boolean;
}) {
  const url = `${SITE_URL}${opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": opts.isNews ? "NewsArticle" : "Article",
    headline: opts.headline,
    description: opts.description,
    image: opts.image ?? `${SITE_URL}${ogPath(opts.path)}`,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    datePublished: opts.published,
    dateModified: opts.modified ?? opts.published,
    author: { "@type": "Organization", name: `Le collectif éditorial — ${SITE_NAME}` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    about: { "@id": `${SITE_URL}/#dominique-de-villepin` },
    inLanguage: "fr-FR",
    isAccessibleForFree: true,
    ...(opts.section ? { articleSection: opts.section } : {}),
    ...(opts.keywords?.length ? { keywords: opts.keywords.join(", ") } : {}),
  };
}

function collectionLd(opts: {
  path: string;
  name: string;
  description: string;
  items: { name: string; path: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}${opts.path}#collection`,
    name: opts.name,
    url: `${SITE_URL}${opts.path}`,
    description: opts.description,
    inLanguage: "fr-FR",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: opts.items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${SITE_URL}${item.path}`,
        name: item.name,
      })),
    },
  };
}

/* ───────── Images de partage ───────── */

export function slugOf(path: string): string {
  return path === "/" ? "accueil" : path.replace(/^\//, "").replace(/\//g, "-");
}

export function ogPath(path: string): string {
  return `/og/${slugOf(path)}.jpg`;
}

/* ───────── Résolution ───────── */

function normalize(raw: string): string {
  if (!raw) return "/";
  let path = raw.split("?")[0].split("#")[0];
  if (!path.startsWith("/")) path = "/" + path;
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path || "/";
}

function notFound(path: string): HeadData {
  return {
    path,
    title: withBrand("Page introuvable"),
    description: "La page demandée est introuvable.",
    canonical: `${SITE_URL}${path}`,
    type: "website",
    image: `${SITE_URL}/og/${slugOf(path)}.jpg`,
    imageAlt: SITE_NAME,
    noindex: true,
    jsonLd: [],
  };
}

export function headForPath(rawPath: string): HeadData {
  const path = normalize(rawPath);

  // Articles
  if (path.startsWith("/articles/")) {
    const slug = path.slice("/articles/".length);
    const article = articles.find((item) => item.slug === slug);
    if (!article) return notFound(path);
    return {
      path,
      title: withBrand(article.title),
      description: article.dek,
      canonical: `${SITE_URL}${path}`,
      type: "article",
      image: `${SITE_URL}${ogPath(path)}`,
      imageAlt: article.imageAlt ?? article.title,
      published: article.dateIso,
      modified: article.dateIso,
      jsonLd: [
        articleLd({
          headline: article.title,
          description: article.dek,
          path,
          published: article.dateIso,
          keywords: article.tags,
          section: article.category,
          image: `${SITE_URL}${ogPath(path)}`,
        }),
        breadcrumbLd([
          { name: "Accueil", path: "/" },
          { name: "Articles", path: "/articles" },
          { name: article.title, path },
        ]),
        subjectPersonLd(),
      ],
    };
  }

  // Actualités
  if (path.startsWith("/actualites/")) {
    const slug = path.slice("/actualites/".length);
    const item = actualites.find((entry) => entry.slug === slug);
    if (!item) return notFound(path);
    return {
      path,
      title: withBrand(item.title),
      description: item.chapeau,
      canonical: `${SITE_URL}${path}`,
      type: "article",
      image: `${SITE_URL}${ogPath(path)}`,
      imageAlt: item.title,
      published: item.dateIso,
      modified: item.dateIso,
      jsonLd: [
        articleLd({
          headline: item.title,
          description: item.chapeau,
          path,
          published: item.dateIso,
          isNews: true,
          image: `${SITE_URL}${ogPath(path)}`,
        }),
        breadcrumbLd([
          { name: "Accueil", path: "/" },
          { name: "Actualités", path: "/actualites" },
          { name: item.title, path },
        ]),
        subjectPersonLd(),
      ],
    };
  }

  // Focus
  if (path.startsWith("/focus/")) {
    const slug = path.slice("/focus/".length);
    const item = focus.find((entry) => entry.slug === slug);
    if (!item) return notFound(path);
    return {
      path,
      title: withBrand(item.title),
      description: item.chapeau,
      canonical: `${SITE_URL}${path}`,
      type: "article",
      image: `${SITE_URL}${ogPath(path)}`,
      imageAlt: item.title,
      published: item.dateIso,
      modified: item.dateIso,
      jsonLd: [
        articleLd({
          headline: item.title,
          description: item.chapeau,
          path,
          published: item.dateIso,
          image: `${SITE_URL}${ogPath(path)}`,
        }),
        breadcrumbLd([
          { name: "Accueil", path: "/" },
          { name: "Focus", path: "/focus" },
          { name: item.title, path },
        ]),
        subjectPersonLd(),
      ],
    };
  }

  // Pages fixes
  const statique = STATIC_PAGES[path];
  if (statique) {
    const collections: Record<string, { name: string; items: { name: string; path: string }[] }> = {
      "/articles": {
        name: "Articles et réflexions",
        items: articles.slice(0, 10).map((a) => ({ name: a.title, path: `/articles/${a.slug}` })),
      },
      "/actualites": {
        name: "Actualités",
        items: actualites.slice(0, 10).map((a) => ({ name: a.title, path: `/actualites/${a.slug}` })),
      },
      "/focus": {
        name: "Focus",
        items: focus.map((f) => ({ name: f.title, path: `/focus/${f.slug}` })),
      },
    };
    const collection = collections[path];
    return {
      path,
      title: statique.title,
      description: statique.description,
      canonical: `${SITE_URL}${path}`,
      type: "website",
      image: `${SITE_URL}${ogPath(path)}`,
      imageAlt: statique.title,
      jsonLd: [
        organizationLd(),
        websiteLd(),
        ...(collection
          ? [
              collectionLd({
                path,
                name: collection.name,
                description: statique.description,
                items: collection.items,
              }),
            ]
          : []),
        ...(path === "/"
          ? [
              breadcrumbLd([{ name: "Accueil", path: "/" }]),
            ]
          : [
              breadcrumbLd([
                { name: "Accueil", path: "/" },
                { name: statique.title.split(" | ")[0], path },
              ]),
            ]),
      ],
    };
  }

  return notFound(path);
}

export type SitemapEntry = { path: string; lastmod: string; changefreq: string; priority: string };

function latest(dates: string[]): string {
  return dates.filter(Boolean).sort().slice(-1)[0] ?? "2026-10-30";
}

/** Toutes les URL publiables, pour le sitemap, le prérendu et llms.txt. */
export function sitemapEntries(): SitemapEntry[] {
  const recentArticles = latest(articles.map((a) => a.dateIso));
  const recentActualites = latest(actualites.map((a) => a.dateIso));
  const entries: SitemapEntry[] = [
    { path: "/", lastmod: recentArticles, changefreq: "daily", priority: "1.0" },
    { path: "/manifeste", lastmod: "2026-07-27", changefreq: "monthly", priority: "0.9" },
    { path: "/articles", lastmod: recentArticles, changefreq: "daily", priority: "0.9" },
    { path: "/actualites", lastmod: recentActualites, changefreq: "daily", priority: "0.9" },
    { path: "/focus", lastmod: latest(focus.map((f) => f.dateIso)), changefreq: "weekly", priority: "0.8" },
    { path: "/capsules", lastmod: "2026-10-27", changefreq: "weekly", priority: "0.7" },
    { path: "/qui-sommes-nous", lastmod: "2026-07-27", changefreq: "yearly", priority: "0.5" },
    { path: "/mentions-legales", lastmod: "2026-07-27", changefreq: "yearly", priority: "0.2" },
    { path: "/confidentialite", lastmod: "2026-07-27", changefreq: "yearly", priority: "0.2" },
  ];
  for (const a of articles) {
    entries.push({ path: `/articles/${a.slug}`, lastmod: a.dateIso, changefreq: "monthly", priority: "0.8" });
  }
  for (const a of actualites) {
    entries.push({ path: `/actualites/${a.slug}`, lastmod: a.dateIso, changefreq: "monthly", priority: "0.7" });
  }
  for (const f of focus) {
    entries.push({ path: `/focus/${f.slug}`, lastmod: f.dateIso, changefreq: "monthly", priority: "0.7" });
  }
  return entries;
}

export function allPaths(): string[] {
  return sitemapEntries().map((entry) => entry.path);
}

/** Contenu de llms.txt, généré depuis les mêmes données. */
export function llmsTxt(): string {
  const lignes: string[] = [
    `# ${SITE_NAME}`,
    "",
    "> Initiative citoyenne indépendante. Tribunes, actualités sourcées et capsules vidéo pour que la voix de la France libre, juste et indépendante porte à nouveau.",
    "",
    "Contact : contact@mavoixpourvillepin.fr. Le site n'est affilié ni à Dominique de Villepin ni à aucun parti politique.",
    "",
    "## Pages",
    "",
    `- [Accueil](${SITE_URL}/)`,
    `- [Manifeste](${SITE_URL}/manifeste)`,
    `- [Articles et réflexions](${SITE_URL}/articles)`,
    `- [Actualités](${SITE_URL}/actualites)`,
    `- [Focus](${SITE_URL}/focus)`,
    `- [Capsules vidéo](${SITE_URL}/capsules)`,
    `- [Qui sommes-nous](${SITE_URL}/qui-sommes-nous)`,
    "",
    "## Articles",
    "",
    ...articles.map((a) => `- [${a.title}](${SITE_URL}/articles/${a.slug})`),
    "",
    "## Actualités",
    "",
    ...actualites.map((a) => `- [${a.title}](${SITE_URL}/actualites/${a.slug}) — ${a.date}`),
    "",
    "## Focus",
    "",
    ...focus.map((f) => `- [${f.title}](${SITE_URL}/focus/${f.slug}) — ${f.date}`),
    "",
  ];
  return lignes.join("\n");
}
